<template>
	<div class="messages">
		<div class="row">
			<div class="col-5 mb-4">
				<h1>{{ $t("change-log") }}</h1>
			</div>
		</div>

		<div class="row mb-3">
			<div class="col-md-6 mb-2 mb-md-0">
				<b-input-group>
					<b-form-input
						v-model="search"
						:placeholder="$t('search-changelog-ellipses')"
						@input="onSearchInput"
					/>
					<b-input-group-append v-if="search">
						<b-button variant="outline-secondary" @click="clearSearch">
							<b-icon icon="x" />
						</b-button>
					</b-input-group-append>
				</b-input-group>
			</div>
			<div class="col-md-6 d-flex align-items-center">
				<b-button-group>
					<b-button
						:variant="selectedType === null ? 'secondary' : 'outline-secondary'"
						size="sm"
						@click="setType(null)"
					>{{ $t('all') }}</b-button>
					<b-button
						v-for="t in types"
						:key="t.value"
						:variant="selectedType === t.value ? t.variant : ('outline-' + t.variant)"
						size="sm"
						@click="setType(t.value)"
					>{{ $t(t.value) }}</b-button>
				</b-button-group>
			</div>
		</div>

		<ScrollPaginator
			ref="paginator"
			:data="updates"
			:submitting="submitting"
			:filters="paginatorFilters"
			@toPage="toPage"
		>
			<template v-slot:item="{ item }">
				<div class="list-group border-radius-2 no-border mb-3 shadow-sm">
					<div class="list-group-item list-group-header">
						<div class="d-flex align-items-center justify-content-between mb-1">
							<h3 class="mb-0"><strong>{{ item.title }}</strong></h3>
							<div class="d-flex align-items-center ml-3 flex-shrink-0">
								<b-badge v-if="item.type" :variant="typeVariant(item.type)" class="mr-2">{{ $t(item.type) }}</b-badge>
								<b-badge variant="primary">v{{ item.version }}</b-badge>
							</div>
						</div>
						<p class="mb-2 text-muted small">
							<b-icon icon="calendar3" class="mr-1" />
							{{ $datetime.getLocaleStringFromTimeStamp(item.datetime) }}
						</p>
						<p class="mb-0 changelog-description">
							{{ displayDescription(item) }}
						</p>
						<a
							v-if="item.description.length > 220"
							href="#"
							class="small"
							@click.prevent="toggleExpand(item.id)"
						>
							{{ isExpanded(item.id) ? $t('read-less') : $t('read-more') }}
						</a>
					</div>
				</div>
			</template>
			<template v-slot:skeletonLoader>
				<div v-for="n in 4" :key="n" class="list-group border-radius-2 no-border mb-3 shadow-sm">
					<div class="list-group-item list-group-header">
						<div class="d-flex align-items-center justify-content-between mb-2">
							<div class="skeleton-line" style="width: 45%;" />
							<div class="skeleton-line" style="width: 60px;" />
						</div>
						<div class="skeleton-line mb-3" style="width: 30%;" />
						<div class="skeleton-line mb-1" style="width: 100%;" />
						<div class="skeleton-line" style="width: 70%;" />
					</div>
				</div>
			</template>
		</ScrollPaginator>
	</div>
</template>

<script>
import ScrollPaginator from "src/components/Table/ScrollPaginator.vue";

export default {
	components: {
		ScrollPaginator,
	},
	data() {
		return {
			updates: [],
			pagination: { page: 1, page_size: 15 },
			search: '',
			selectedType: null,
			submitting: true,
			expandedItems: {},
			searchTimeout: null,
			types: [
				{ value: 'bug_fix', variant: 'warning' },
				{ value: 'new_feature', variant: 'success' },
				{ value: 'major_release', variant: 'danger' }
			],
			latestRequestId: 0, 
		};
	},
	mounted() {
		this.getChangelog();
	},
	methods: {
		getChangelog() {

			const requestId = ++this.latestRequestId;

			const data = {
				page: this.pagination.page,
				page_size: this.pagination.page_size,
			};
			if (this.search) data.search = this.search;
			if (this.selectedType) data.type = this.selectedType;

			this.submitting = true;

			this.$cfetch(`${this.$settings.APIBaseURL}/changelog/list`, {
				method: "post",
				data,
			})
				.then((res) => {
					if (requestId !== this.latestRequestId) return;
					if (res.status === "SUCCESS") {
						this.updates = res.results;
						this.pagination = res.pagination;
						this.submitting = false;
					}
				})
				.catch((error) => {
					this.submitting = false;
					console.log(error);
					this.$log.error.call(this, error);
				});
		},

		toPage(pageNo) {
			this.pagination.page = pageNo;
			this.getChangelog();
		},

		applyFilters() {
			this.pagination.page = 1;
			if (this.$refs.paginator) this.$refs.paginator.resetScroll();
			this.getChangelog();
		},

		onSearchInput() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => this.applyFilters(), 400);
		},

		clearSearch() {
			this.search = '';
			this.applyFilters();
		},

		setType(type) {
			this.selectedType = type;
			this.applyFilters();
		},

		isExpanded(id) {
			return !!this.expandedItems[id];
		},

		toggleExpand(id) {
			this.$set(this.expandedItems, id, !this.expandedItems[id]);
		},

		typeVariant(type) {
			const variants = {
				bug_fix: 'warning',
				new_feature: 'success',
				major_release: 'danger'
			};
			return variants[type] || 'secondary';
		},

		displayDescription(item) {
			if (this.isExpanded(item.id) || item.description.length <= 220) {
				return item.description;
			}
			return item.description.slice(0, 220) + '…';
		}
	},
	computed: {
		paginatorFilters() {
			return {
				page: this.pagination.page,
				page_size: this.pagination.page_size,
				total: this.pagination.total,
				search: this.search,
				type: this.selectedType,
			};
		},
},
};
</script>

<style scoped>
.skeleton-line {
	height: 14px;
	background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
	background-size: 200% 100%;
	animation: skeleton-shimmer 1.5s infinite;
	border-radius: 4px;
}

@keyframes skeleton-shimmer {
	0% { background-position: 200% 0; }
	100% { background-position: -200% 0; }
}

.changelog-description {
	white-space: pre-line;
}
</style>
